// @flow
import { createSelector } from "reselect";
import consts, { STATUSES } from "../data/consts";
import type {
	GoogleDriveState,
	AssetsParams,
	GoogleDriveRecord,
} from "../data/types";
import { selectGoogleDrive, selectListFiles, selectCurrentContext } from ".";


const selectNavigationParams: GoogleDriveState => AssetsParams = createSelector(
	selectGoogleDrive,
	(gdrive: GoogleDriveRecord) => gdrive.lists[consts.CONTEXTS.ASSETS].params,
);

export const selectNavigationFolderId: GoogleDriveState => ?string = createSelector(
	selectNavigationParams,
	selectCurrentContext,
	(params, context) => (context === consts.CONTEXTS.ASSETS ? params.id : null),
);

export const selectNavigationPreviousFolder: GoogleDriveState => ?Object = createSelector(
	selectNavigationParams,
	(params) => params.previousFolder,
);

export const selectNavigationDriveId: GoogleDriveState => ?string = createSelector(
	selectNavigationParams,
	(params) => params.driveId,
);

export const selectNavigationKind: GoogleDriveState => string = createSelector(
	selectNavigationParams,
	(params) => params.kind,
);

export const selectNavigationPath: Object => string[] = createSelector(
	selectNavigationParams,
	(params) => params.path,
);

const selectListFolders: GoogleDriveState => Object = createSelector(
	selectGoogleDrive,
	selectCurrentContext,
	(gdrive, context) => gdrive.lists[context].folders,
);

export const selectFolders: GoogleDriveState => Object[] = createSelector(
	selectListFolders,
	(folders) => folders.data || [],
);

export const selectFiles: GoogleDriveState => Object[] = createSelector(
	selectListFiles,
	(files) => files.data || [],
);

export const selectSelectedFiles: GoogleDriveState => string[] = createSelector(
	selectGoogleDrive,
	selectCurrentContext,
	(gdrive, context) => gdrive.lists[context].selected,
);

export const selectFilesCursor: Object => ?string = createSelector(
	selectListFiles,
	(files) => files.cursor,
);

const selectFoldersStatus: Object => string = createSelector(
	selectListFolders,
	(folders) => folders.status,
);

const selectFilesStatus: Object => string = createSelector(
	selectListFiles,
	(files) => files.status,
);

export const selectFoldersCursor: Object => ?string = createSelector(
	selectListFolders,
	(folders) => folders.cursor,
);

export const selectFoldersIsPending: Object => boolean = createSelector(
	selectFoldersStatus,
	(status) => status === STATUSES.PENDING,
);

export const selectFoldersLoadingIsDone: Object => boolean = createSelector(
	selectFoldersStatus,
	selectFoldersCursor,
	(status, cursor) => status === STATUSES.SUCCESS && !cursor,
);

export const selectFilesIsPending: Object => boolean = createSelector(
	selectFilesStatus,
	(status) => status === STATUSES.PENDING,
);

export const selectFilesIsInit: Object => boolean = createSelector(
	selectFilesStatus,
	(status) => status === undefined,
);

export const selectIsLoading: Object => boolean = createSelector(
	selectFoldersIsPending,
	selectFilesIsPending,
	selectFolders,
	selectFiles,
	(folderIsPending, filesIsPending, folders, files) => (folderIsPending && !folders.length)
        || (!files.length && filesIsPending && !folders.length),
);

export const selectIsNoResults: Object => boolean = createSelector(
	selectFoldersStatus,
	selectFilesStatus,
	selectFolders,
	selectFiles,
	(foldersStatus, filesStatus, folders, files) => (!foldersStatus || foldersStatus !== STATUSES.PENDING)
        && !folders.length
        && (!filesStatus || filesStatus !== STATUSES.PENDING)
        && !files.length,
);

export const selectListRequestId: GoogleDriveState => number = createSelector(
	selectGoogleDrive,
	(gdrive: GoogleDriveRecord) => gdrive.lists.requestId,
);
